/**
 * @ims/shared — Config Resolver
 *
 * Loads and merges all 4 config layers (Brand, Model, Colors, Market, Intersection)
 * into a single ResolvedConfig ready for the frontend and engine.
 *
 * Algorithm:
 *   1. Load all 5 sources in parallel (no inter-dependencies)
 *   2. Check model availability in market
 *   3. Derive GLB URL (lhd/rhd from market.steeringWheel)
 *   4. Enrich colors with translated names (from intersection.colorNames)
 *   5. Merge feature flags: model → market → intersection (ascending priority)
 *
 * @see docs/ims-brand-frontends/02-config-system.md — Resolution-Algorithmus
 */

import type {
  BrandConfig,
  ModelConfig,
  ColorsConfig,
  MarketConfig,
  IntersectionConfig,
  PresetConfig,
  FeatureFlags,
  ResolvedConfig,
  ResolvedColorDefinition,
  ResolvedInteriorColorDefinition,
  ResolvedRimSet,
  ResolvedPanoVariant,
  UITranslations,
} from '../types/config';
import { loadConfig } from './configLoader';
import { ConfigError } from './configLoader';

// ---------------------------------------------------------------------------
// ResolveOptions
// ---------------------------------------------------------------------------

export interface ResolveOptions {
  /**
   * Base URL for content files. Default: '' (relative to current origin).
   * Example: 'https://cdn.example.com'
   */
  baseUrl?: string;
  /**
   * When true, each config layer is validated against its JSON Schema.
   * Adds a small overhead at load time. Recommended in development.
   * Default: false
   */
  validate?: boolean;
  /**
   * Preset ID to load from `content/brands/{brand}/presets/{id}.json`.
   * When set, the preset layer is applied on top of all other layers.
   * Missing or invalid preset files are silently ignored (fallback to standard config).
   */
  presetId?: string;
  /**
   * Active language code (BCP-47, e.g. "en"). When set and the market has
   * `uiByLang[lang]`, those strings are merged over `market.ui`. Also used
   * to resolve `intersection.rimLabelsByLang[lang]` for rim display names.
   */
  lang?: string;
}

// ---------------------------------------------------------------------------
// resolveConfig
// ---------------------------------------------------------------------------

/**
 * Load and resolve all 4 config layers for a given brand × model × market.
 *
 * @throws {ConfigError} CONFIG_NOT_FOUND — any config file is missing
 * @throws {ConfigError} CONFIG_VALIDATION_ERROR — a config fails schema validation
 * @throws {ConfigError} CONFIG_PARSE_ERROR — a config file contains invalid JSON
 * @throws {Error} MODEL_NOT_IN_MARKET — model is not in market.availableModels
 *
 * @example
 * const config = await resolveConfig('audi', 'q3sportback', 'de', {
 *   baseUrl: '/content',
 *   validate: import.meta.env.DEV,
 * });
 * createEngine({ container, modelUrl: config.glbUrl, features: config.features });
 */
export async function resolveConfig(
  brandId: string,
  modelId: string,
  marketId: string,
  options: ResolveOptions = {},
): Promise<ResolvedConfig> {
  const { baseUrl = '', validate = false, presetId, lang } = options;
  const base = baseUrl.replace(/\/$/, ''); // strip trailing slash

  // Build URLs
  const brandUrl = `${base}/content/brands/${brandId}/brand.json`;
  const brandColorsUrl = `${base}/content/brands/${brandId}/colors.json`;
  const modelUrl = `${base}/content/models/${brandId}/${modelId}/model.json`;
  const marketUrl = `${base}/content/markets/${brandId}/${marketId}.json`;
  const intersectionUrl = `${base}/content/intersections/${brandId}/${modelId}/${marketId}.json`;
  const presetUrl = presetId ? `${base}/content/brands/${brandId}/presets/${presetId}.json` : null;

  // 1. Load all layers in parallel — no inter-dependencies
  //    Preset is loaded alongside but failures are silently ignored (fallback to no-preset).
  const [brand, model, brandColors, market, intersection, preset] = await Promise.all([
    loadConfig<BrandConfig>(brandUrl, validate ? 'brand' : undefined),
    loadConfig<ModelConfig>(modelUrl, validate ? 'model' : undefined),
    loadConfig<ColorsConfig>(brandColorsUrl, validate ? 'colors' : undefined),
    loadConfig<MarketConfig>(marketUrl, validate ? 'market' : undefined),
    loadConfig<IntersectionConfig>(intersectionUrl, validate ? 'intersection' : undefined),
    presetUrl ? loadConfig<PresetConfig>(presetUrl).catch(() => null) : Promise.resolve(null),
  ]);

  // 2. Model availability check
  if (market.availableModels.length > 0 && !market.availableModels.includes(modelId)) {
    throw new ConfigError(
      'CONFIG_NOT_FOUND',
      `Model "${modelId}" is not available in market "${marketId}" (brand: ${brandId}). Available: ${market.availableModels.join(', ')}`,
      marketUrl,
    );
  }

  // 3. Steering side → GLB URL + thumbnail
  const side = market.steeringWheel;
  const glbUrl =
    side === 'rhd' && model.assets.glb.rhd ? model.assets.glb.rhd : model.assets.glb.lhd;

  const rawThumb = model.assets.thumbnail;
  const thumbnail =
    rawThumb === undefined
      ? undefined
      : typeof rawThumb === 'string'
        ? rawThumb
        : side === 'rhd' && rawThumb.rhd
          ? rawThumb.rhd
          : rawThumb.lhd;

  // 4. Filter brand colors to the model's declared list (order preserved), then enrich
  //    with translated display names from the intersection layer.
  //    Fallback: all brand colors when exteriorColorIds is omitted.
  const brandColorIndex = new Map(brandColors.exteriorColors.map((c) => [c.id, c]));
  const filteredColorDefs = model.exteriorColorIds
    ? model.exteriorColorIds.flatMap((id) => {
        const entry = brandColorIndex.get(id);
        if (!entry) {
          console.warn(
            `[configResolver] exteriorColorId "${id}" not found in brand colors library (${brandColorsUrl}). Skipping.`,
          );
          return [];
        }
        return [entry];
      })
    : brandColors.exteriorColors;

  const colors: ResolvedColorDefinition[] = filteredColorDefs.map((color) => {
    const { secondary, ...rest } = color;
    const resolved: ResolvedColorDefinition = {
      ...rest,
      displayName: intersection.colorNames[color.id] ?? color.id,
      primary: {
        ...color.primary,
        materials: color.primary.materials ?? model.paintMaterials?.primary ?? [],
      },
    };
    if (secondary) {
      resolved.secondary = {
        ...secondary,
        materials: secondary.materials ?? model.paintMaterials?.secondary ?? [],
      };
    }
    return resolved;
  });

  // 5. Enrich interior colors with translated display names
  const interiorColors: ResolvedInteriorColorDefinition[] = (brandColors.interiorColors ?? []).map(
    (ic) => ({
      ...ic,
      displayName: intersection.interiorColorNames?.[ic.id] ?? ic.displayName ?? ic.id,
    }),
  );

  // 6. Feature flag resolution: Model → Market → Intersection → Preset (ascending priority)
  const features: FeatureFlags = {
    ...model.features,
    ...(market.features ?? {}),
    ...(intersection.overrides?.features ?? {}),
    ...(preset?.overrides?.features ?? {}),
  };

  // 7. Resolve rim sets — enrich definitions with market-translated display names.
  //    Source priority: rimLabelsByLang[lang] → rimLabels → ModelRimSet.label → id (fallback).
  const rims: ResolvedRimSet[] = (model.rims?.sets ?? []).map((set) => ({
    ...set,
    displayName:
      (lang ? intersection.rimLabelsByLang?.[lang]?.[set.id] : undefined) ??
      intersection.rimLabels?.[set.id] ??
      set.label ??
      set.id,
  }));

  // 8. Resolve pano variants — enrich with market-translated display names.
  //    When model.panoVariants is absent but assets.panoUrl is set, synthesise a
  //    single variant so the rest of the system always has a uniform array to work with.
  const rawPanoVariants =
    model.panoVariants ??
    (model.assets.panoUrl ? [{ id: 'default', url: model.assets.panoUrl, label: 'Interior' }] : []);
  const panoVariants: ResolvedPanoVariant[] = rawPanoVariants.map((v) => ({
    ...v,
    displayName: intersection.panoLabels?.[v.id] ?? v.label ?? v.id,
  }));

  // 9. Apply preset overrides — filter colors/interiors when preset restricts them.
  const presetColorIds = preset?.overrides?.exteriorColorIds;
  const finalColors = presetColorIds ? colors.filter((c) => presetColorIds.includes(c.id)) : colors;

  const presetInteriorIds = preset?.overrides?.interiorVariantIds;
  const finalInteriorColors = presetInteriorIds
    ? interiorColors.filter((ic) => presetInteriorIds.includes(ic.id))
    : interiorColors;

  // 10. Apply preset allowDesktop override on brand (shallow copy to avoid mutation).
  const finalBrand =
    preset?.allowDesktop !== undefined ? { ...brand, allowDesktop: preset.allowDesktop } : brand;

  // 11. Resolve effective UI: merge uiByLang[lang] on top of market.ui when a language
  //     override is active. Only the fields present in uiByLang[lang] are overridden —
  //     this allows partial translations (only override what differs).
  const effectiveUi: UITranslations =
    lang && market.uiByLang?.[lang] ? { ...market.ui, ...market.uiByLang[lang] } : market.ui;

  // 12. Resolve effective cookie texts: merge cookiesByLang[lang] on top of market.cookies.
  const effectiveCookies =
    lang && market.cookiesByLang?.[lang]
      ? { ...market.cookies, ...market.cookiesByLang[lang] }
      : market.cookies;

  // 13. Resolve effective app disclaimer.
  const effectiveDisclaimer =
    lang && market.appDisclaimerByLang?.[lang]
      ? market.appDisclaimerByLang[lang]
      : market.appDisclaimer;

  // 14. Resolve effective intersection CTA: merge ctaByLang[lang] over cta when a
  //     language override is active. Translates per-intersection CTA labels/URLs.
  const effectiveIntersection =
    lang && intersection.ctaByLang?.[lang]
      ? { ...intersection, cta: { ...intersection.cta, ...intersection.ctaByLang[lang] } }
      : intersection;

  return {
    brand: finalBrand,
    model,
    colors: finalColors,
    interiorColors: finalInteriorColors,
    rims,
    panoVariants,
    market,
    intersection: effectiveIntersection,
    features,
    glbUrl,
    thumbnail,
    ui: effectiveUi,
    cookies: effectiveCookies,
    appDisclaimer: effectiveDisclaimer,
    preset: preset ?? undefined,
  };
}
